import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import type { ListProps, TableHeaders, TableProps } from '@fourthwall/components'
import {
  ConfirmModal,
  EmptyState,
  PositionChangeModal,
  useMediaQueryContext,
  useModalContext,
  usePagination,
  useToastsContext,
} from '@fourthwall/components'
import { useSeriesQuery } from '@modules/Dashboard/Memberships/modules/Series/hooks/useSeriesQuery'
import { reverse, routing } from '@utils/routing'

import { useDeleteSingleSeriesMutation } from '../../../SingleSeries/hooks/useDeleteSingleSeriesMutation'
import { SeriesCell } from '../../components/SeriesCell'
import { useReorderSingleSeriesMutation } from '../../hooks/useReorderSingleSeriesMutation'
import type { SingleSeries } from './types'
import { getCurrentSeriesPositionIndex, getUpdatedSeriesPositionIndex } from './utils'

type Props = { children: ReactNode }

type SeriesContextType = {
  table: TableProps<SingleSeries>
  list: ListProps
  pageCount: number
  currentPage: number
  setCurrentPage: (page: number) => void
}

export const SeriesContext = createContext<SeriesContextType | undefined>(undefined)

export const SeriesProvider = ({ children }: Props) => {
  const { hasPermission } = useDashboardPermissions()
  const [currentPage, setCurrentPage] = usePagination()

  const modal = useModalContext()
  const toast = useToastsContext()
  const media = useMediaQueryContext()

  const { seriesQuery } = useSeriesQuery([{ page: currentPage, postsType: 'video', limit: 25 }])
  const { reorderSingleSeriesMutation } = useReorderSingleSeriesMutation()
  const { deleteSingleSeriesMutation } = useDeleteSingleSeriesMutation()

  const getSingleSeriesLink = ({ id }: SingleSeries) => ({
    pathname: reverse(routing.memberships.series.singleSeries.self, { singleSeriesId: id }),
    search: location.search,
  })

  const handleChangePosition = useCallback(
    (singleSeries: SingleSeries) => {
      if (!seriesQuery.data) return

      modal.open(PositionChangeModal, {
        title: 'Change series position',
        maxPosition: seriesQuery.data.pagination.total,
        currentPosition: getCurrentSeriesPositionIndex(
          singleSeries.id,
          seriesQuery.data.series,
          currentPage,
          9,
        ),
        onSubmit: (formValues) => {
          const { type = 'EXACT', exactPosition } = formValues

          reorderSingleSeriesMutation.mutateAsync(
            [
              {
                id: singleSeries.id,
              },
              {
                position: getUpdatedSeriesPositionIndex(
                  type,
                  exactPosition,
                  seriesQuery.data.pagination.total,
                ),
              },
            ],
            {
              onSuccess: () => {
                seriesQuery.refetch()
              },
            },
          )
        },
      })
    },
    [seriesQuery, modal, reorderSingleSeriesMutation, currentPage],
  )

  const handleDelete = useCallback(
    (singleSeries: SingleSeries) => {
      modal.open(ConfirmModal, {
        title: 'Delete series',
        text: 'Are you sure you want to delete this series?',
        onConfirm: () => {
          deleteSingleSeriesMutation.mutateAsync([{ id: singleSeries.id }], {
            onSuccess: () => {
              seriesQuery.refetch()
              toast.notify('Series deleted successfully', {
                type: 'info',
              })
            },
          })
        },
      })
    },

    [seriesQuery, modal, deleteSingleSeriesMutation, toast],
  )

  const tableHeaders: TableHeaders<SingleSeries> = useMemo(
    () => [
      {
        id: 'series',
        label: 'Series',
        render: (singleSeries) => {
          return (
            <SeriesCell
              title={singleSeries.title}
              quantity={singleSeries.episodesCount}
              thumbnailUrl={singleSeries.thumbnail?.url || singleSeries.coverImage?.url}
            />
          )
        },
      },
    ],
    [],
  )

  const table: TableProps<SingleSeries> = useMemo(
    () => ({
      data: seriesQuery.data?.series || [],
      headers: tableHeaders,
      isLoading:
        seriesQuery.isFetching ||
        reorderSingleSeriesMutation.isLoading ||
        deleteSingleSeriesMutation.isLoading,
      sortable: true,
      actions: [
        {
          label: 'Change position',
          onClick: (singleSeries) => handleChangePosition(singleSeries as SingleSeries),
        },
        {
          label: 'Delete',
          onClick: (singleSeries) => handleDelete(singleSeries as SingleSeries),
        },
      ],
      onPositionChange: (oldIndex, newIndex) => {
        reorderSingleSeriesMutation.mutateAsync(
          [
            {
              id: seriesQuery.data?.series[oldIndex].id!,
            },
            {
              position: newIndex,
            },
          ],
          {
            onSuccess: () => {
              seriesQuery.refetch()
            },
          },
        )
      },
      emptyComponent: (
        <EmptyState
          variant={media.tablet ? 'primary' : 'secondary'}
          title="You haven’t created any video series yet"
          label={hasPermission('memberships.video.create') ? 'Add new video series' : undefined}
          to={routing.memberships.series.create}
        />
      ),
      rowComponent: Link,
      getRowLink: (singleSeries) => ({
        pathname: reverse(routing.memberships.series.singleSeries.self, {
          singleSeriesId: singleSeries.id,
        }),
        search: location.search,
      }),
    }),
    [
      seriesQuery,
      media,
      tableHeaders,
      hasPermission,
      reorderSingleSeriesMutation,
      handleChangePosition,
      deleteSingleSeriesMutation,
      handleDelete,
    ],
  )

  const list: ListProps = useMemo(
    () => ({
      data: seriesQuery.data?.series || [],
      isLoading: seriesQuery.isInitialLoading,
      emptyComponent: (
        <EmptyState
          variant={media.tablet ? 'primary' : 'secondary'}
          title="You haven’t created any video series yet"
          label={hasPermission('memberships.video.create') ? 'Add new video series' : undefined}
          to={routing.memberships.series.create}
        />
      ),
      renderItem: (singleSeries: SingleSeries) => (
        <SeriesCell
          title={singleSeries.title}
          quantity={singleSeries.episodesCount}
          thumbnailUrl={singleSeries.thumbnail?.url || singleSeries.coverImage?.url}
          options={[
            {
              label: 'Change position',
              onClick: () => {
                handleChangePosition(singleSeries)
              },
            },
          ]}
        />
      ),
      getItemLink: getSingleSeriesLink,
    }),
    [seriesQuery, media, hasPermission, handleChangePosition],
  )

  const value = useMemo(
    () => ({
      table,
      list,
      pageCount: seriesQuery.data?.pagination?.pages || 1,
      currentPage,
      setCurrentPage,
    }),
    [table, list, currentPage, setCurrentPage, seriesQuery],
  )

  return <SeriesContext.Provider value={value}>{children}</SeriesContext.Provider>
}
